import React, { useState, useEffect } from 'react';
import { 
  X, 
  Calendar, 
  Type, 
  Save,
  Loader2,
  Tag,
  Plus,
  Trash2
} from 'lucide-react';
import styles from './HolidayDrawer.module.css';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import CustomDatePicker from '../../../components/Common/DatePicker/CustomDatePicker';
import CustomSelect from '../../../components/Common/Select/CustomSelect';

const HolidayDrawer = ({ isOpen, onClose, onSave, holiday = null }) => {
  const [holidays, setHolidays] = useState([{
    date: '',
    title: '',
    type: 'public'
  }]);

  const [loading, setLoading] = useState(false);

  const holidayTypes = [
    { value: 'Public Holiday', label: 'Public Holiday' },
    { value: 'Company Holiday', label: 'Company Holiday' },
    { value: 'Week Off Day', label: 'Weekly Off' }
  ];

  useEffect(() => {
    if (holiday && isOpen) {
      setHolidays([{
        date: holiday.date || '',
        title: holiday.holiday_title || holiday.title || '',
        type: holiday.holiday_type || holiday.type || 'Public Holiday'
      }]);
    } else if (!holiday) {
      setHolidays([{
        date: '',
        title: '',
        type: 'Public Holiday'
      }]);
    }
  }, [isOpen, holiday]);

  const addHolidayRow = () => {
    setHolidays([...holidays, { date: '', title: '', type: 'Public Holiday' }]);
  };

  const removeHolidayRow = (index) => {
    if (holidays.length === 1) {
      toast.error("At least one holiday is required");
      return;
    }
    const updatedHolidays = holidays.filter((_, i) => i !== index);
    setHolidays(updatedHolidays);
  };

  const handleInputChange = (index, field, value) => {
    const updatedHolidays = [...holidays];
    updatedHolidays[index][field] = value;
    setHolidays(updatedHolidays);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const isValid = holidays.every(h => h.date && h.title && h.type);
    if (!isValid) {
      toast.error("Please fill all required fields for all holidays");
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const employee_id = user.id;

    if (!employee_id) {
      toast.error("User session not found. Please re-login.");
      return;
    }

    setLoading(true);
    try {
      if (holiday) {
        // Edit mode (single update)
        const payload = {
          date: holidays[0].date,
          holiday_title: holidays[0].title,
          holiday_type: holidays[0].type,
          employee_id: employee_id
        };
        await api.put(`/holidays/${holiday.id}`, payload);
        toast.success('Holiday updated successfully');
      } else {
        // Create mode (multiple)
        const payload = {
          employee_id: employee_id,
          holidays: holidays.map(h => ({
            date: h.date,
            holiday_title: h.title,
            holiday_type: h.type
          }))
        };
        await api.post('/holidays/bulk', payload);
        toast.success(`${holidays.length} Holidays added successfully`);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving holidays:', error);
      toast.error(error.response?.data?.message || 'Failed to save holidays');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{holiday ? 'Edit Holiday' : 'Add Multiple Holidays'}</h2>
            <p>Configure organizational holidays and weekly offs.</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {holidays.map((item, index) => (
              <div key={index} className={styles['holiday-row-card']}>
                <div className={styles['row-header']}>
                  <span className={styles['row-number']}>Holiday #{index + 1}</span>
                  {!holiday && holidays.length > 1 && (
                    <button 
                      type="button" 
                      className={styles['remove-row-btn']}
                      onClick={() => removeHolidayRow(index)}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                <div className={styles['row-content']}>
                  {/* Holiday Date */}
                  <div className={styles['form-group']}>
                    <label><Calendar size={16} /> Holiday Date <span className={styles['required-star']}>*</span></label>
                    <CustomDatePicker
                      selected={item.date}
                      onChange={(date) => handleInputChange(index, 'date', date ? date.toLocaleDateString('en-CA') : '')}
                      placeholderText="Select date"
                      required
                    />
                  </div>

                  {/* Holiday Title */}
                  <div className={styles['form-group']}>
                    <label><Type size={16} /> Holiday Title <span className={styles['required-star']}>*</span></label>
                    <input 
                      type="text" 
                      value={item.title}
                      onChange={(e) => handleInputChange(index, 'title', e.target.value)}
                      placeholder="e.g. Independence Day"
                      required
                      className={styles['form-input']}
                    />
                  </div>

                  {/* Holiday Type */}
                  <div className={styles['form-group']}>
                    <label><Tag size={16} /> Holiday Type <span className={styles['required-star']}>*</span></label>
                    <CustomSelect 
                      value={holidayTypes.find(t => t.value === item.type)}
                      onChange={(opt) => handleInputChange(index, 'type', opt ? opt.value : '')}
                      options={holidayTypes}
                      placeholder="Select Type"
                      required
                    />
                  </div>
                </div>
              </div>
            ))}

            {!holiday && (
              <button 
                type="button" 
                className={styles['add-row-btn']}
                onClick={addHolidayRow}
              >
                <Plus size={18} />
                <span>Add Another Holiday</span>
              </button>
            )}
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{holiday ? 'Update Holiday' : `Save ${holidays.length} Holidays`}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HolidayDrawer;
